import { Button } from '@mdvip/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@mdvip/shared/components/ui/dialog';
import { ChevronLeft } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAddBankAccount } from '@/hooks/payments/useAddBankAccount';
import { useAddPaymentCard } from '@/hooks/payments/useAddPaymentCard';
import { useMemberBillingProfile } from '@/hooks/payments/useMemberBillingProfile';
import { usePaymentAccountForm } from '@/hooks/payments/usePaymentAccountForm';
import type {
  AddBankAccountInput,
  AddPaymentCardInput,
  BillingAddressValues,
  PaymentTab,
} from '@/lib/payments/paymentAccount.types';
import { PaymentMethodForm } from './PaymentMethodForm';
import { PaymentReviewScreen } from './PaymentReviewScreen';

export interface AddPaymentAccountModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Initial tab; lets callers open straight to Credit Card or Bank Account. */
  defaultTab?: PaymentTab;
  /**
   * Called after a successful submit. The caller decides what happens next (close + toast,
   * return to the AutoPay flow, select the new account in the pay modal, …) — the modal does
   * not assume. Keeps it reusable across the Payment Accounts page, AutoPay, and one-time pay.
   */
  onSuccess?: () => void;
}

/**
 * Add Payment Account modal (CNP-133). Owns the single RHF instance + screen/tab state, hosts
 * Screen 1 (entry) and Screen 2 (review), and wires the verify→create submit through the
 * stub hooks. Standalone for now; structured so AutoPay/one-time-pay can reuse it additively.
 */
export function AddPaymentAccountModal({
  open,
  onOpenChange,
  defaultTab = 'credit-card',
  onSuccess,
}: AddPaymentAccountModalProps) {
  const { t } = useTranslation('members');
  const [screen, setScreen] = useState<'form' | 'review'>('form');
  const [activeTab, setActiveTab] = useState<PaymentTab>(defaultTab);
  // Default closed. Visibility/requirement derive from this + savedAddress so they react
  // correctly once the (async) profile loads — never seeded from a not-yet-loaded address.
  const [billingFormOpen, setBillingFormOpen] = useState(false);

  const profile = useMemberBillingProfile().data;
  const savedAddress = profile?.savedAddress ?? null;
  const billingRequired = !savedAddress || billingFormOpen;

  const form = usePaymentAccountForm({ activeTab, billingRequired });

  // Return to a clean entry form: clears all fields (incl. PAN/CVV from memory) and collapses the
  // billing sub-form. Used on dismiss, on successful submit, and on each open.
  const resetModal = useCallback(() => {
    form.reset();
    setScreen('form');
    setBillingFormOpen(false);
  }, [form]);

  // The page mounts a single modal and only flips `open`/`defaultTab` (and closes it
  // programmatically after submit). Resetting on every open — not just on dismiss — re-syncs the
  // launch tab and guarantees no stale screen or retained data leaks into the next open.
  useEffect(() => {
    if (open) {
      setActiveTab(defaultTab);
      resetModal();
    }
  }, [open, defaultTab, resetModal]);

  const handleSubmitSuccess = () => {
    onSuccess?.();
    resetModal(); // clear card data from memory immediately after a successful add (PCI)
  };
  const cardMutation = useAddPaymentCard({ onSuccess: handleSubmitSuccess });
  const bankMutation = useAddBankAccount({ onSuccess: handleSubmitSuccess });
  const isSubmitting = cardMutation.isPending || bankMutation.isPending;

  // The billing address that will be submitted: the entered sub-form when required/open,
  // otherwise the member's saved address.
  const effectiveBilling: BillingAddressValues = billingRequired
    ? form.getValues('billing')
    : (savedAddress ?? form.getValues('billing'));

  const handleOpenChange = (next: boolean) => {
    // Dismiss discards everything (AC: X / overlay discards data).
    if (!next) resetModal();
    onOpenChange(next);
  };

  // Closing the "add new" billing form discards what was entered (matches the design's Discard).
  const handleToggleBillingForm = () => {
    if (billingFormOpen) form.resetField('billing');
    setBillingFormOpen((isOpen) => !isOpen);
  };

  const handleConfirm = () => {
    const values = form.getValues();
    if (activeTab === 'credit-card') {
      const input: AddPaymentCardInput = { ...values.card, billing: effectiveBilling };
      cardMutation.submit(input);
    } else {
      const input: AddBankAccountInput = { ...values.bank, billing: effectiveBilling };
      bankMutation.submit(input);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* Overlay-scroll layout (design system): the overlay scrolls the tall form, page locked —
          rather than the panel scrolling internally. */}
      <DialogContent scrollable className="sm:max-w-2xl">
        {screen === 'review' && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="absolute top-4 left-4"
            aria-label={t('payments.addAccount.reviewScreen.back')}
            onClick={() => setScreen('form')}
          >
            <ChevronLeft />
          </Button>
        )}
        <DialogHeader className={screen === 'review' ? 'pl-10' : undefined}>
          <DialogTitle>
            {screen === 'form' ? t('payments.addAccount.title') : t('payments.addAccount.reviewScreen.title')}
          </DialogTitle>
          {screen === 'review' && (
            <DialogDescription>{t('payments.addAccount.reviewScreen.description')}</DialogDescription>
          )}
        </DialogHeader>

        <FormProvider {...form}>
          {screen === 'form' ? (
            <PaymentMethodForm
              activeTab={activeTab}
              onTabChange={setActiveTab}
              onAdvance={() => setScreen('review')}
              onCancel={() => handleOpenChange(false)}
              autoPayEnrolled={profile?.autoPayEnrolled ?? false}
              savedAddress={savedAddress}
              billingFormOpen={billingFormOpen}
              onToggleBillingForm={handleToggleBillingForm}
            />
          ) : (
            <PaymentReviewScreen
              activeTab={activeTab}
              autoPayAmount={profile?.autoPayAmount ?? null}
              autoPayPlan={profile?.autoPayPlan ?? null}
              billing={effectiveBilling}
              isSubmitting={isSubmitting}
              onMakeChanges={() => setScreen('form')}
              onConfirm={handleConfirm}
            />
          )}
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
